import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type AuditEntry } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconShield } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { CustomerDrawer } from '@/components/admin/CustomerDrawer';
import { DriverDrawer } from '@/components/admin/DriverDrawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

// The trust-&-safety audit actions this page surfaces (emitted by admin-svc's
// blockban handlers). Anything else in the audit trail is filtered out here so
// the page reads as a focused moderation log.
const TS_ACTIONS = new Set(['customer.block', 'customer.unblock', 'driver.block', 'driver.unblock']);

// action → { label, tone } for the badge. block/ban is danger; reinstate is ok.
const ACTION_META: Record<string, { label: string; tone: BadgeTone }> = {
  'customer.block': { label: 'Customer blocked', tone: 'danger' },
  'customer.unblock': { label: 'Customer reinstated', tone: 'ok' },
  'driver.block': { label: 'Driver banned', tone: 'danger' },
  'driver.unblock': { label: 'Driver reinstated', tone: 'ok' },
};

// We pull a generous slice of the audit trail and filter client-side — the
// audit endpoint isn't action-filterable and the trust-&-safety volume is low,
// so this keeps the page dependency-free without a new backend param.
const FETCH_LIMIT = 200;

type Selected = { scope: 'customer' | 'driver'; id: number } | null;

/** Trust & Safety — the moderation log: every block / ban / reinstate staff
 *  action (from admin_audit_log), newest first. Each row deep-links to the
 *  affected customer/driver drawer, where the actual block/ban control lives
 *  (gated by customers.block / drivers.ban). Read-access is gated by audit.read;
 *  the write controls inside the drawers are independently gated. */
export function Safety() {
  const [selected, setSelected] = useState<Selected>(null);
  // The block/ban controls inside the drawers need ops/superadmin; surface a
  // small hint to read-only roles so the page doesn't look "broken" to them.
  // Both hooks must run unconditionally (rules-of-hooks) — combine after.
  const canBlock = useAdminPermission('customers.block');
  const canBan = useAdminPermission('drivers.ban');
  const canModerate = canBlock || canBan;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'audit', 'safety', FETCH_LIMIT],
    queryFn: ({ signal }) => adminApi.audit({ limit: FETCH_LIMIT }, signal),
  });

  // Filter to the trust-&-safety actions, preserving the server's newest-first
  // order.
  const rows = useMemo(
    () => (data?.items ?? []).filter((e) => TS_ACTIONS.has(e.action)),
    [data],
  );

  // Resolve a row's affected account (scope + numeric id) from the audit
  // target. blockban writes target_type = scope ('customer'|'driver') and
  // target_id = the numeric user id.
  const accountOf = (e: AuditEntry): Selected => {
    const scope = e.target_type === 'driver' ? 'driver' : e.target_type === 'customer' ? 'customer' : null;
    const id = Number(e.target_id);
    if (!scope || !Number.isFinite(id) || id <= 0) return null;
    return { scope, id };
  };

  const columns: Array<Column<AuditEntry>> = [
    {
      key: 'action',
      header: 'Action',
      cell: (r) => {
        const m = ACTION_META[r.action] ?? { label: r.action, tone: 'neutral' as BadgeTone };
        return <Badge tone={m.tone}>{m.label}</Badge>;
      },
    },
    {
      key: 'target',
      header: 'Account',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {r.target_type}#{r.target_id}
        </span>
      ),
    },
    { key: 'actor', header: 'By', cell: (r) => <span className="text-xs text-slate-300">{r.actor_email || '—'}</span> },
    {
      key: 'result',
      header: 'Result',
      cell: (r) => <Badge tone={r.result === 'ok' ? 'ok' : 'danger'} dot>{r.result}</Badge>,
    },
    { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => {
        const acct = accountOf(r);
        if (!acct) return <span className="text-xs text-muted">—</span>;
        return (
          <button
            type="button"
            onClick={() => setSelected(acct)}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
          >
            Open {acct.scope}
          </button>
        );
      },
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Trust & Safety"
        subtitle="Moderation log — customer blocks and driver bans, newest first. Open an account to block, ban, or reinstate."
      />

      {!canModerate ? (
        <Card>
          <p className="text-xs text-muted">
            Read-only — your role can review this moderation log but cannot block, ban, or reinstate accounts.
            (Those actions require the <code className="text-slate-400">ops</code> role.)
          </p>
        </Card>
      ) : null}

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading moderation log…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load the moderation log" description={(error as Error)?.message} />
        ) : (
          <DataTable<AuditEntry>
            columns={columns}
            rows={rows}
            rowKey={(r) => String(r.id)}
            stickyHeader
            empty={
              <EmptyState
                title="No moderation actions yet"
                description="No customer has been blocked and no driver banned. Block or ban from a customer/driver drawer; actions appear here."
                icon={<IconShield className="h-6 w-6" />}
              />
            }
          />
        )}
      </Card>

      {selected?.scope === 'customer' ? (
        <CustomerDrawer id={selected.id} onClose={() => setSelected(null)} />
      ) : selected?.scope === 'driver' ? (
        <DriverDrawer id={selected.id} onClose={() => setSelected(null)} />
      ) : null}
    </AdminPage>
  );
}
